import { z } from 'zod'

/** Familiarity is the one learning state a person sees; it is the Concept's status.familiarity. */
export const FAMILIARITIES = ['new', 'learning', 'known'] as const
export type Familiarity = (typeof FAMILIARITIES)[number]

/** The label that groups a course's resources into a track (for example design-patterns). */
export const TRACK = 'track'

const webLink = z
  .string()
  .max(2000)
  .regex(/^https?:\/\//, 'use a web link')

/** The course's track is the label `track`, not a field. */
export const courseSpec = z
  .object({
    title: z.string().min(1, 'title is required').max(200),
    source_url: webLink.optional(),
    goal: z.string().max(2000).optional(),
  })
  .strict()
export type CourseSpec = z.infer<typeof courseSpec>

/** progress_pct and last_session_at are written from L2 on, and unset until then. */
export const courseStatus = z.object({
  state: z.enum(['active', 'paused', 'done']),
  progress_pct: z.number().int().optional(),
  last_session_at: z.string().optional(),
})
export type CourseStatus = z.infer<typeof courseStatus>

export const moduleSpec = z
  .object({
    title: z.string().min(1, 'title is required').max(200),
    /** ranks siblings: edges carry no order */
    order: z.number().int().min(0).optional(),
    summary: z.string().max(2000).optional(),
  })
  .strict()
export type ModuleSpec = z.infer<typeof moduleSpec>

/** The lesson counts are written from L2 on, and unset until then. */
export const moduleStatus = z.object({
  state: z.enum(['active', 'paused', 'done']),
  lessons_done: z.number().int().optional(),
  lessons_total: z.number().int().optional(),
})
export type ModuleStatus = z.infer<typeof moduleStatus>

export const KIND_HINTS = ['read', 'watch', 'exercise'] as const

export const lessonSpec = z
  .object({
    title: z.string().min(1, 'title is required').max(200),
    order: z.number().int().min(0).optional(),
    url: webLink.optional(),
    estimate_minutes: z.number().int().min(1).max(1440).optional(),
    kind_hint: z.enum(KIND_HINTS).optional(),
  })
  .strict()
export type LessonSpec = z.infer<typeof lessonSpec>

export const lessonStatus = z.object({
  state: z.enum(['todo', 'done', 'skipped']),
  completed_at: z.string().optional(),
  actual_minutes: z.number().int().optional(),
})
export type LessonStatus = z.infer<typeof lessonStatus>

export const conceptSpec = z
  .object({
    name: z.string().min(1, 'name is required').max(200),
    category: z.string().max(60).optional(),
    one_liner: z.string().max(500).optional(),
    source_url: webLink.optional(),
    my_notes: z.string().max(20000).optional(),
    when_to_use: z.string().max(5000).optional(),
    when_not_to: z.string().max(5000).optional(),
  })
  .strict()
export type ConceptSpec = z.infer<typeof conceptSpec>

/**
 * next_review_at, interval_days, ease and promoted_at belong to later milestones (the review
 * scheduler, promotion): they exist here and stay unset. Never shown.
 */
export const conceptStatus = z.object({
  familiarity: z.enum(FAMILIARITIES),
  next_review_at: z.string().optional(),
  interval_days: z.number().int().optional(),
  ease: z.number().optional(),
  promoted_at: z.string().optional(),
  /** the answered_at of the newest answer the scheduler has replayed */
  reviewed_at: z.string().optional(),
  last_touched: z.string(),
})
export type ConceptStatus = z.infer<typeof conceptStatus>

export const SESSION_MODES = ['lesson', 'review', 'spot', 'test'] as const
export type SessionMode = (typeof SESSION_MODES)[number]

/** One sitting with Learn. What it went through is its `covers` edges. */
export const sessionSpec = z
  .object({
    started_at: z.string(),
    planned_minutes: z.number().int().min(1).max(1440).optional(),
    mode: z.enum(SESSION_MODES),
  })
  .strict()
export type SessionSpec = z.infer<typeof sessionSpec>

/** A session with no ended_at is still open. */
export const sessionStatus = z.object({
  ended_at: z.string().optional(),
  actual_minutes: z.number().int().optional(),
  items_done: z.number().int().optional(),
  /** how the deck's answers went, counted as they were saved */
  right: z.number().int().optional(),
  partial: z.number().int().optional(),
  wrong: z.number().int().optional(),
})
export type SessionStatus = z.infer<typeof sessionStatus>

export const EXERCISE_FORMATS = ['learn', 'judge', 'quick_pick', 'spot'] as const
export type ExerciseFormat = (typeof EXERCISE_FORMATS)[number]
export const RESULTS = ['right', 'partial', 'wrong'] as const
export type Result = (typeof RESULTS)[number]

export const choice = z.object({ label: z.string().min(1).max(80), correct: z.boolean().optional() }).strict()
export type Choice = z.infer<typeof choice>

/** A card that teaches or asks. Its concepts are `exercises` edges; concept_ref is the first of them. */
export const exerciseSpec = z
  .object({
    concept_ref: z.string().max(64).optional(),
    format: z.enum(EXERCISE_FORMATS),
    prompt: z.string().min(1).max(2000),
    code_excerpt: z.string().max(4000).optional(),
    choices: z.array(choice).max(8).optional(),
    multi_select: z.boolean().optional(),
    time_limit_s: z.number().int().min(0).max(300).optional(),
    repo: z.string().max(300).optional(),
    path: z.string().max(500).optional(),
    generated_by: z.string().max(60).optional(),
    explanation: z.string().max(500).optional(),
    order: z.number().int().min(0).optional(),
  })
  .strict()
export type ExerciseSpec = z.infer<typeof exerciseSpec>

export const exerciseStatus = z.object({
  state: z.enum(['open', 'answered']),
  result: z.enum(RESULTS).optional(),
  answered_at: z.string().optional(),
})
export type ExerciseStatus = z.infer<typeof exerciseStatus>
